import type { QueryClient } from "@tanstack/react-query";

import type { HubCacheSseEvent } from "@/lib/hubCacheChannels";
import {
  hubPresenceCurrentQueryKey,
  type HubPresenceCurrentResponse,
  type HubPresenceViewer,
} from "@/lib/hubPresenceApi";
import { applyHubNotificationsUnreadSseEvent } from "@/lib/notificationsUnreadCountLive";
import { notificationsUnreadCountQueryKey } from "@/lib/notificationsApi";

export type HubPresenceTabStreamConnectedEvent = {
  phase: "connected";
  tabId: string;
  routeKey?: string;
  unread_count?: number;
  viewers?: HubPresenceViewer[];
};

export function isHubPresenceTabStreamConnectedEvent(
  event: HubCacheSseEvent | HubPresenceTabStreamConnectedEvent,
): event is HubPresenceTabStreamConnectedEvent {
  return event.phase === "connected" && "tabId" in event;
}

export function applyHubPresenceTabStreamConnectedSnapshot(
  queryClient: QueryClient,
  userId: string | null,
  event: HubCacheSseEvent | HubPresenceTabStreamConnectedEvent,
): { appliedUnread: boolean; appliedViewers: boolean } {
  if (!isHubPresenceTabStreamConnectedEvent(event)) {
    return { appliedUnread: false, appliedViewers: false };
  }

  let appliedUnread = false;
  if (typeof event.unread_count === "number" && Number.isFinite(event.unread_count)) {
    queryClient.setQueryData(notificationsUnreadCountQueryKey, {
      unreadCount: Math.max(0, Math.floor(event.unread_count)),
    });
    appliedUnread = true;
  } else {
    appliedUnread = applyHubNotificationsUnreadSseEvent(queryClient, event);
  }

  let appliedViewers = false;
  if (
    userId != null &&
    userId !== "" &&
    typeof event.routeKey === "string" &&
    event.routeKey !== "" &&
    Array.isArray(event.viewers)
  ) {
    const data: HubPresenceCurrentResponse = {
      routeKey: event.routeKey,
      viewers: event.viewers,
    };
    queryClient.setQueryData(hubPresenceCurrentQueryKey(userId, event.routeKey), data);
    appliedViewers = true;
  }

  return { appliedUnread, appliedViewers };
}
